import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Pencil,
  Plus,
  Thermometer,
  Trash2,
  TrendingUp,
  Wheat,
} from "lucide-react";
import { useMemo, useState } from "react";
import { FlourBlendScience } from "./FlourScience";
import { EmptyState, Modal } from "./Primitives";
import { getFlourProfile, liftLabel, rateLabel } from "../data/flourProfiles";
import {
  curvePath,
  estimateStarterPeak,
  FLOUR_TYPES,
  starterCalibration,
} from "../lib/fermentationModel";

function localDateTimeValue(date = new Date()) {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 16);
}

function dateKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function feedCalendarCells(month) {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return Array.from({ length: 42 }, (_, index) => {
    const day = index - first.getDay() + 1;
    return day >= 1 && day <= days ? day : null;
  });
}

function addHours(value, hours) {
  const date = new Date(value);
  const offset = Number(hours);
  if (Number.isNaN(date.getTime()) || !Number.isFinite(offset)) return null;
  return new Date(date.getTime() + offset * 60 * 60 * 1000);
}

function formatShortTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function feedAgainWindowHours(peakHours, temperature = 76) {
  const peakValue = Number(peakHours);
  if (!Number.isFinite(peakValue) || peakValue <= 0) return 4;
  const temp = Number(temperature || 76);
  const tempFactor = temp >= 78 ? 0.28 : temp <= 68 ? 0.46 : 0.36;
  return Math.max(2, Math.min(8, peakValue * tempFactor));
}

function peakHoursForLog(log, starter, calibration) {
  const loggedPeak = Number(log.peakHours);
  if (Number.isFinite(loggedPeak) && loggedPeak > 0) {
    return { hours: loggedPeak, source: "logged" };
  }
  const estimate = estimateStarterPeak({
    ratio: log.ratio || "1:2:2",
    temperature: log.temperature || 76,
    flourBlend: log.flourBlend || starter?.flourBlend,
    hydration: starter?.hydration,
    calibration,
  });
  return { hours: estimate.hours, source: "estimated" };
}

function buildFeedCalendarEvents(logs, starter, calibration) {
  const eventsByDate = new Map();

  function addEvent(event) {
    const key = dateKey(event.time);
    if (!key) return;
    const items = eventsByDate.get(key) || [];
    items.push(event);
    items.sort((a, b) => a.sort - b.sort);
    eventsByDate.set(key, items);
  }

  logs.forEach((log) => {
    const feedDate = new Date(log.dateTime);
    if (Number.isNaN(feedDate.getTime())) return;

    if (log.entryType === "rise") {
      addEvent({
        id: `${log.id || log.dateTime}-rise`,
        type: "rise",
        label: "Rise check",
        time: feedDate,
        timeLabel: formatShortTime(feedDate),
        detail: log.peakHours ? `${log.peakHours}h peak observed` : log.rise ? `${formatRisePercent(log.rise)} rise` : `${log.temperature}°F check`,
        sort: feedDate.getTime(),
        ...(log.rise && !log.peakHours ? { detail: `${formatRisePercent(log.rise)} rise` } : {}),
      });
      return;
    }

    const peakEstimate = peakHoursForLog(log, starter, calibration);
    const peakAt = addHours(feedDate, peakEstimate.hours);
    const feedAgainAt = addHours(feedDate, peakEstimate.hours + feedAgainWindowHours(peakEstimate.hours, log.temperature));

    addEvent({
      id: `${log.id || log.dateTime}-feed`,
      type: "feed",
      label: "Fed",
      time: feedDate,
      timeLabel: formatShortTime(feedDate),
      detail: log.ratio || "Feed logged",
      sort: feedDate.getTime(),
    });

    if (peakAt) {
      addEvent({
        id: `${log.id || log.dateTime}-peak`,
        type: "peak",
        label: "Peak",
        time: peakAt,
        timeLabel: formatShortTime(peakAt),
        detail: `${peakEstimate.hours.toFixed(1)}h ${peakEstimate.source}`,
        sort: peakAt.getTime(),
      });
    }

    if (feedAgainAt) {
      addEvent({
        id: `${log.id || log.dateTime}-refeed`,
        type: "refeed",
        label: "Feed again",
        time: feedAgainAt,
        timeLabel: formatShortTime(feedAgainAt),
        detail: "Starter should be ripe or beginning to recede",
        sort: feedAgainAt.getTime(),
      });
    }
  });

  return eventsByDate;
}

function calendarDaySummary(events) {
  return events.map((event) => `${event.label} ${event.timeLabel}: ${event.detail}`).join("; ");
}

const HOUR_MS = 60 * 60 * 1000;

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function formatDuration(hours) {
  const value = Number(hours);
  if (!Number.isFinite(value)) return "—";
  if (Math.abs(value) < 1) return `${Math.max(1, Math.round(Math.abs(value) * 60))}m`;
  if (Math.abs(value) >= 24) return `${(value / 24).toFixed(1)}d`;
  return `${value.toFixed(1)}h`;
}

function formatScheduleTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatBlend(blend = []) {
  if (!blend?.length) return "No flour logged";
  return blend.map((item) => {
    const profile = getFlourProfile(item.type || "Bread flour");
    return `${item.percent ?? 100}% ${profile.name || item.type}`;
  }).join(" · ");
}

function logFlourLabel(log, starter) {
  return formatBlend(log?.flourBlend?.length ? log.flourBlend : starter?.flourBlend);
}

function average(values) {
  const usable = values.map(Number).filter((value) => Number.isFinite(value));
  if (!usable.length) return null;
  return usable.reduce((sum, value) => sum + value, 0) / usable.length;
}

function risePercentFromMultiplier(value) {
  const multiplier = Number(value);
  if (!Number.isFinite(multiplier) || multiplier <= 0) return null;
  return Math.max(0, (multiplier - 1) * 100);
}

function riseMultiplierFromPercent(value) {
  const percent = Number(value);
  if (!Number.isFinite(percent) || percent < 0) return null;
  return 1 + percent / 100;
}

function formatRisePercent(value) {
  const percent = risePercentFromMultiplier(value);
  if (percent === null) return "—";
  return `${Math.round(percent)}%`;
}

function formatElapsedSinceFeed(hours) {
  const totalMinutes = Math.max(0, Math.round(Number(hours) * 60));
  const wholeHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!wholeHours) return `${minutes} minutes`;
  if (!minutes) return `${wholeHours} hour${wholeHours === 1 ? "" : "s"}`;
  return `${wholeHours}h ${minutes}m`;
}

function buildStarterReadiness(logs, starter, calibration) {
  const feedLogs = logs.filter((log) => log.entryType !== "rise");
  const latestFeed = feedLogs[0];

  if (!latestFeed) {
    return {
      status: "Needs first feed",
      tone: "quiet",
      stageLabel: "No feed baseline",
      progress: 0,
      summary: "Log one feed with ratio, flour, temperature, and rise notes so the app can start timing this starter.",
      nextAction: "Tap Log feed after your next feeding.",
      meta: [
        { label: "Calibration", value: "Waiting" },
        { label: "Best use", value: "Log a feed first" },
        { label: "Flour", value: formatBlend(starter?.flourBlend) },
      ],
    };
  }

  const feedAt = new Date(latestFeed.dateTime);
  const now = new Date();
  const peakEstimate = peakHoursForLog(latestFeed, starter, calibration);
  const feedAgainHours = feedAgainWindowHours(peakEstimate.hours, latestFeed.temperature);
  const peakAt = addHours(feedAt, peakEstimate.hours);
  const feedAgainAt = addHours(feedAt, peakEstimate.hours + feedAgainHours);
  const elapsed = (now.getTime() - feedAt.getTime()) / HOUR_MS;
  const progress = clampNumber((elapsed / peakEstimate.hours) * 100, 0, 100);
  const latestRiseAfterFeed = logs.find((log) => {
    if (!Number(log.rise) && !Number(log.peakHours)) return false;
    const logDate = new Date(log.dateTime);
    return !Number.isNaN(logDate.getTime()) && logDate >= feedAt;
  });
  const loggedRise = Number(latestRiseAfterFeed?.rise);
  const loggedPeak = Number(latestRiseAfterFeed?.peakHours);
  const temp = Number(latestRiseAfterFeed?.temperature ?? latestFeed.temperature ?? 76);
  const hasStrongRise = Number.isFinite(loggedRise) && loggedRise >= 1.8;
  const hasPeakLog = Number.isFinite(loggedPeak) && loggedPeak > 0;

  let status = "Still building";
  let tone = "building";
  let stageLabel = `${Math.round(progress)}% to expected peak`;
  let nextAction = peakAt ? `Check again near ${formatScheduleTime(peakAt)}.` : "Check rise and aroma again soon.";

  if (elapsed < -0.25) {
    status = "Scheduled feed";
    tone = "quiet";
    stageLabel = "Feed is in the future";
    nextAction = `Feed at ${formatScheduleTime(feedAt)}.`;
  } else if (hasStrongRise || hasPeakLog || (elapsed >= peakEstimate.hours * 0.82 && elapsed <= peakEstimate.hours + feedAgainHours * 0.55)) {
    status = "Ready window";
    tone = "ready";
    stageLabel = hasStrongRise ? `${formatRisePercent(loggedRise)} rise logged` : "Expected peak window";
    nextAction = "Use for dough now, chill it, or feed again if you are not baking.";
    if (hasStrongRise) stageLabel = `${formatRisePercent(loggedRise)} rise logged`;
  } else if (elapsed > peakEstimate.hours + feedAgainHours) {
    status = "Past peak";
    tone = "past";
    stageLabel = `${formatDuration(elapsed - peakEstimate.hours)} past peak`;
    nextAction = "Feed again for strength, or use for a tangier lower-lift bake.";
  } else if (elapsed >= peakEstimate.hours + feedAgainHours * 0.55) {
    status = "Feed again soon";
    tone = "caution";
    stageLabel = "Leaving peak window";
    nextAction = feedAgainAt ? `Feed again by about ${formatScheduleTime(feedAgainAt)}.` : "Feed again soon.";
  } else if ((temp <= 68 || calibration > 1.15) && elapsed >= peakEstimate.hours * 0.55) {
    status = "Slow build";
    tone = "caution";
    stageLabel = temp <= 68 ? `${temp}°F is cool` : `${calibration.toFixed(2)}× slower than base`;
    nextAction = "Give it warmth or more time before using it as your main leaven.";
  }

  const summary = `${starter?.name || "Starter"} was fed ${formatDuration(elapsed)} ago with ${latestFeed.ratio || "a saved ratio"}. Expected peak is ${formatDuration(peakEstimate.hours)} after feeding.`;

  return {
    status,
    tone,
    stageLabel,
    progress: Math.round(progress),
    summary,
    nextAction,
    meta: [
      { label: "Fed", value: formatScheduleTime(feedAt) },
      { label: "Expected peak", value: formatScheduleTime(peakAt) },
      { label: "Feed again", value: formatScheduleTime(feedAgainAt) },
      { label: "Flour", value: logFlourLabel(latestFeed, starter) },
    ],
  };
}

function buildStarterCalibrationSummary(logs, starter, calibration) {
  const peakSamples = logs.filter((log) => Number(log.peakHours) > 0).slice(0, 8);
  const riseSamples = logs.filter((log) => Number(log.rise) > 0).slice(0, 12);
  const avgPeak = average(peakSamples.map((log) => log.peakHours));
  const peakSpread = avgPeak
    ? average(peakSamples.map((log) => Math.abs(Number(log.peakHours) - avgPeak)))
    : null;
  const strongRiseCount = riseSamples.filter((log) => Number(log.rise) >= 1.8).length;

  const flourGroups = new Map();
  logs.filter((log) => Number(log.rise) > 0 || Number(log.peakHours) > 0).forEach((log) => {
    const label = logFlourLabel(log, starter);
    const group = flourGroups.get(label) || { label, count: 0, rises: [], peaks: [] };
    group.count += 1;
    if (Number(log.rise) > 0) group.rises.push(Number(log.rise));
    if (Number(log.peakHours) > 0) group.peaks.push(Number(log.peakHours));
    flourGroups.set(label, group);
  });
  const bestFlour = [...flourGroups.values()].sort((a, b) => {
    const aRise = average(a.rises) || 0;
    const bRise = average(b.rises) || 0;
    return bRise - aRise || b.count - a.count;
  })[0];

  const warmPeak = average(peakSamples.filter((log) => Number(log.temperature) >= 76).map((log) => log.peakHours));
  const coolPeak = average(peakSamples.filter((log) => Number(log.temperature) <= 72).map((log) => log.peakHours));
  const confidence = clampNumber(
    peakSamples.length * 12 + riseSamples.length * 5 + (peakSpread !== null && peakSpread <= 1.25 ? 14 : 0),
    0,
    100,
  );
  const confidenceLabel = confidence >= 72 ? "High confidence" : confidence >= 38 ? "Medium confidence" : "Low confidence";
  const calibrationNote = calibration > 1.12
    ? `${starter?.name || "This starter"} trends slower than the base model, so the app stretches peak timing.`
    : calibration < 0.9
      ? `${starter?.name || "This starter"} trends faster than the base model, so the app shortens peak timing.`
      : `${starter?.name || "This starter"} is close to the base model; more peak logs will sharpen it.`;

  return {
    confidence,
    confidenceLabel,
    note: calibrationNote,
    metrics: [
      {
        label: "Peak logs",
        value: `${peakSamples.length}/8`,
        detail: avgPeak ? `${avgPeak.toFixed(1)}h average peak${peakSpread !== null ? ` · ${peakSpread.toFixed(1)}h drift` : ""}` : "Add peak hours when you log rise.",
      },
      {
        label: "Rise reliability",
        value: riseSamples.length ? `${strongRiseCount}/${riseSamples.length}` : "0 checks",
        detail: riseSamples.length ? "Rise checks at 80% or higher count as ready signals." : "Log rise checks between feed and peak.",
      },
      {
        label: "Flour response",
        value: bestFlour ? `${bestFlour.count} logs` : "No data",
        detail: bestFlour ? `${bestFlour.label}${average(bestFlour.rises) ? ` · ${formatRisePercent(average(bestFlour.rises))} avg rise` : ""}` : "Pick flour type with each feed.",
      },
      {
        label: "Temperature effect",
        value: warmPeak && coolPeak ? `${(coolPeak - warmPeak).toFixed(1)}h swing` : "Learning",
        detail: warmPeak && coolPeak ? `Warm logs average ${warmPeak.toFixed(1)}h; cool logs average ${coolPeak.toFixed(1)}h.` : "Log both cool and warm kitchen days to learn your house curve.",
      },
    ],
  };
}

function emptyProfile() {
  return {
    id: `starter-${Date.now()}`,
    name: "",
    hydration: 100,
    primaryFlour: "Bread flour",
    primaryPercent: 70,
    secondaryFlour: "Whole wheat",
    secondaryPercent: 30,
    hasSecondaryFlour: true,
    notes: "",
    isNew: true,
  };
}

function profileForm(starter) {
  return {
    ...starter,
    primaryFlour: getFlourProfile(starter.flourBlend?.[0]?.type || "Bread flour").name,
    primaryPercent: starter.flourBlend?.[0]?.percent ?? 100,
    secondaryFlour: getFlourProfile(starter.flourBlend?.[1]?.type || "Whole wheat").name,
    secondaryPercent: starter.flourBlend?.[1]?.percent ?? 0,
    hasSecondaryFlour: Boolean(starter.flourBlend?.[1]),
    isNew: false,
  };
}

export function StarterLab({
  starters,
  starterLogs,
  onSaveStarter,
  onDeleteStarter,
  onStarterLogged,
  onDeleteStarterLog,
}) {
  const [selectedId, setSelectedId] = useState(starters[0]?.id || "");
  const [profileModal, setProfileModal] = useState(null);
  const [feedModal, setFeedModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const selected = starters.find((starter) => starter.id === selectedId) || starters[0];
  const logs = useMemo(() => [...starterLogs]
    .filter((log) => log.starterId === selected?.id || (!log.starterId && selected?.id === starters[0]?.id))
    .sort((a, b) => new Date(b.dateTime || 0) - new Date(a.dateTime || 0)), [selected?.id, starterLogs, starters]);
  const latest = logs[0];
  const latestFeed = useMemo(() => logs.find((log) => log.entryType !== "rise"), [logs]);
  const latestRise = useMemo(() => logs.find((log) => Number(log.rise) > 0 || log.entryType === "rise"), [logs]);
  const recentFeedForRise = useMemo(() => {
    if (feedModal?.mode !== "rise") return null;
    const checkAt = new Date(feedModal.dateTime);
    if (Number.isNaN(checkAt.getTime())) return null;
    const priorFeed = logs.find((log) => {
      if (log.entryType === "rise") return false;
      const fedAt = new Date(log.dateTime);
      return !Number.isNaN(fedAt.getTime()) && fedAt <= checkAt;
    });
    if (!priorFeed) return null;
    const fedAt = new Date(priorFeed.dateTime);
    const hours = (checkAt.getTime() - fedAt.getTime()) / HOUR_MS;
    if (hours < 0 || hours > 12) return null;
    return { feed: priorFeed, fedAt, hours };
  }, [feedModal?.dateTime, feedModal?.mode, logs]);
  const calibration = starterCalibration(starterLogs, selected?.id);
  const calibrationLogCount = useMemo(() => starterLogs
    .filter((log) => log.starterId === selected?.id && Number(log.peakHours) > 0)
    .slice(0, 8)
    .length, [selected?.id, starterLogs]);
  const calibrationLabel = calibrationLogCount
    ? `${calibrationLogCount} peak log${calibrationLogCount === 1 ? "" : "s"} · ${calibration.toFixed(2)}× calibrated`
    : "0 peak logs · default curve";
  const peak = estimateStarterPeak({
    ratio: latestFeed?.ratio || latest?.ratio || "1:2:2",
    temperature: latest?.temperature || latestFeed?.temperature || 76,
    flourBlend: latestFeed?.flourBlend || latest?.flourBlend || selected?.flourBlend,
    hydration: selected?.hydration,
    calibration,
  });
  const readiness = useMemo(() => buildStarterReadiness(logs, selected, calibration), [logs, selected, calibration]);
  const calibrationSummary = useMemo(() => buildStarterCalibrationSummary(logs, selected, calibration), [logs, selected, calibration]);
  const starterCurve = curvePath(peak.hours, "starter");
  const cells = feedCalendarCells(calendarMonth);
  const eventsByDate = useMemo(() => buildFeedCalendarEvents(logs, selected, calibration), [logs, selected, calibration]);

  function openFeed(date, mode = "feed") {
    const dateTime = date
      ? `${date}T08:00`
      : localDateTimeValue();
    setFeedModal({
      mode,
      starterId: selected?.id,
      dateTime,
      ratio: latestFeed?.ratio || latest?.ratio || "1:2:2",
      temperature: latest?.temperature || latestFeed?.temperature || 76,
      rise: "",
      peakHours: "",
      flourType: latestFeed?.flourBlend?.[0]?.type || latest?.flourBlend?.[0]?.type || selected?.flourBlend?.[0]?.type || "Bread flour",
      note: "",
    });
  }

  function openRise(date) {
    openFeed(date, "rise");
  }

  function saveProfile(event) {
    event.preventDefault();
    const secondaryValue = profileModal.hasSecondaryFlour ? Number(profileModal.secondaryPercent) : 0;
    const total = Number(profileModal.primaryPercent) + secondaryValue;
    const primaryPercent = total ? Math.round(Number(profileModal.primaryPercent) / total * 100) : 100;
    const secondaryPercent = profileModal.hasSecondaryFlour ? 100 - primaryPercent : 0;
    const saved = {
      id: profileModal.id,
      name: profileModal.name.trim(),
      hydration: Number(profileModal.hydration),
      flourBlend: [
        { type: profileModal.primaryFlour, percent: primaryPercent },
        ...(secondaryPercent > 0 ? [{ type: profileModal.secondaryFlour, percent: secondaryPercent }] : []),
      ],
      notes: profileModal.notes.trim(),
      isNew: profileModal.isNew,
    };
    if (!saved.name) return;
    onSaveStarter(saved);
    setSelectedId(saved.id);
    setProfileModal(null);
  }

  function saveFeed(event) {
    event.preventDefault();
    const { flourType, mode, ...savedFeed } = feedModal;
    onStarterLogged({
      ...savedFeed,
      entryType: mode === "rise" ? "rise" : "feed",
      flourBlend: [{ type: flourType || "Bread flour", percent: 100 }],
      temperature: Number(feedModal.temperature),
      rise: feedModal.rise === "" ? null : riseMultiplierFromPercent(feedModal.rise),
      peakHours: feedModal.peakHours === "" ? null : Number(feedModal.peakHours),
      dateTime: new Date(feedModal.dateTime).toISOString(),
    });
    setFeedModal(null);
  }

  if (!selected) {
    return (
      <section className="starter-lab">
        <EmptyState title="No starters yet" body="Create a starter profile to begin tracking feeds and fermentation." />
        <button className="primary-button" type="button" onClick={() => setProfileModal(emptyProfile())}>Create a starter</button>
        {profileModal ? (
          <StarterProfileModal form={profileModal} setForm={setProfileModal} onClose={() => setProfileModal(null)} onSubmit={saveProfile} />
        ) : null}
      </section>
    );
  }

  return (
    <section className="starter-lab">
      <div className="starter-profile-switcher">
        <label>
          Starter
          <select value={selected.id} onChange={(event) => setSelectedId(event.target.value)}>
            {starters.map((starter) => <option key={starter.id} value={starter.id}>{starter.name}</option>)}
          </select>
        </label>
        <button type="button" className="icon-button subtle" onClick={() => setProfileModal(profileForm(selected))} aria-label={`Edit ${selected.name}`}>
          <Pencil size={17} />
        </button>
        <button type="button" className="round-action small" onClick={() => setProfileModal(emptyProfile())} aria-label="Add starter">
          <Plus size={18} />
        </button>
      </div>

      <div className="starter-hero">
        <span className="starter-jar"><Wheat size={46} /></span>
        <div>
          <p>Your starter</p>
          <h2>{selected.name}</h2>
          <span className="starter-live"><i /> {selected.flourBlend.map((item) => `${item.percent}% ${item.type}`).join(" · ")}</span>
        </div>
      </div>
      {selected.notes ? <p className="starter-profile-note">{selected.notes}</p> : null}

      <div className="starter-reading-grid">
        <div><TrendingUp /><strong>{latestRise?.rise ? formatRisePercent(latestRise.rise) : "—"}</strong><span>last logged rise</span></div>
        <div><Thermometer /><strong>{latest?.temperature ? `${latest.temperature}°F` : "—"}</strong><span>last jar temp</span></div>
        <div><Clock3 /><strong>{peak.hours.toFixed(1)}h</strong><span>estimated peak</span></div>
      </div>
      <div className="starter-log-actions" aria-label={`${selected.name} starter logging shortcuts`}>
        <button type="button" className="starter-log-action feed" onClick={() => openFeed()}>
          <CalendarDays size={16} /> Log feed
        </button>
        <button type="button" className="starter-log-action rise" onClick={() => openRise()}>
          <TrendingUp size={16} /> Log rise
        </button>
      </div>

      <div className="starter-insight-grid">
        <section className={`starter-readiness-card ${readiness.tone}`} aria-label={`${selected.name} readiness`}>
          <div className="starter-insight-header">
            <span>Starter readiness</span>
            <em>{readiness.stageLabel}</em>
          </div>
          <h3>{readiness.status}</h3>
          <p>{readiness.summary}</p>
          <div className="starter-readiness-meter" aria-label={`${readiness.progress}% to expected peak`}>
            <span style={{ width: `${readiness.progress}%` }} />
          </div>
          <div className="starter-readiness-next">
            <Clock3 size={16} />
            <span>{readiness.nextAction}</span>
          </div>
          <div className="starter-readiness-grid">
            {readiness.meta.map((item) => (
              <span key={item.label}>
                <small>{item.label}</small>
                <strong>{item.value}</strong>
              </span>
            ))}
          </div>
        </section>

        <section className="starter-calibration-card" aria-label={`${selected.name} calibration`}>
          <div className="starter-insight-header">
            <span>Calibration view</span>
            <em>{calibration.toFixed(2)}× model</em>
          </div>
          <h3>{calibrationSummary.confidenceLabel}</h3>
          <div className="starter-confidence-bar" aria-label={`${Math.round(calibrationSummary.confidence)}% calibrated`}>
            <span style={{ width: `${calibrationSummary.confidence}%` }} />
          </div>
          <div className="starter-calibration-grid">
            {calibrationSummary.metrics.map((item) => (
              <span key={item.label}>
                <small>{item.label}</small>
                <strong>{item.value}</strong>
                <em>{item.detail}</em>
              </span>
            ))}
          </div>
          <p className="starter-calibration-note">{calibrationSummary.note}</p>
        </section>
      </div>

      <div className="starter-curve">
        <div className="section-title-line">
          <h3>Rise estimate</h3>
          <span>{latestFeed?.ratio || latest?.ratio || "1:2:2"} feed · {calibrationLabel}</span>
        </div>
        <svg viewBox="0 0 300 100" role="img" aria-label={`Estimated starter rise peaking in ${peak.hours.toFixed(1)} hours`}>
          <path className="chart-fill starter-estimate-fill" d={starterCurve.fill} />
          <path className="chart-line" d={starterCurve.line} />
        </svg>
        <div className="chart-labels"><span>Feed</span><span>{(peak.hours / 2).toFixed(1)}h</span><span>Peak {peak.hours.toFixed(1)}h</span></div>
        <div className="starter-flour-signals">
          <span><b>{peak.flourRate.toFixed(2)}×</b> {rateLabel(peak.flourRate)} flour-fed activity</span>
          <span><b>{peak.visibleRiseRate.toFixed(2)}×</b> {liftLabel(peak.visibleRiseRate)} visible jar lift</span>
          <span><b>{peak.waterDemandRate.toFixed(2)}×</b> water demand</span>
        </div>
        <p className="model-note">Estimate uses feed ratio, temperature, flour blend, and your logged peak history. Jar height reflects both gas production and the flour’s ability to hold that gas.</p>
      </div>

      <section className="starter-flour-science">
        <div className="section-title-line"><h3>Starter flour science</h3><span>Growth + rise</span></div>
        <FlourBlendScience compact flours={selected.flourBlend.map((item) => item.type)} />
      </section>

      <section className="feed-calendar" aria-label={`${selected.name} feed calendar`}>
        <div className="calendar-heading compact">
          <button type="button" onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))} aria-label="Previous feed month"><ChevronLeft size={18} /></button>
          <h2>{calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</h2>
          <button type="button" onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))} aria-label="Next feed month"><ChevronRight size={18} /></button>
        </div>
        <div className="calendar-weekdays" aria-hidden="true">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <span key={day}>{day}</span>)}
        </div>
        <div className="feed-calendar-legend" aria-label="Feed calendar markers">
          <span><i className="feed-dot feed" /> Fed</span>
          <span><i className="feed-dot rise" /> Rise check</span>
          <span><i className="feed-dot peak" /> Expected peak</span>
          <span><i className="feed-dot refeed" /> Feed again</span>
        </div>
        <div className="feed-calendar-grid">
          {cells.map((day, index) => {
            if (!day) return <span className="calendar-blank" key={`feed-blank-${index}`} />;
            const key = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            const dayEvents = eventsByDate.get(key) || [];
            const visibleEvents = dayEvents.slice(0, 3);
            const className = [
              "feed-day",
              dayEvents.length ? "has-feed-events" : "",
              dayEvents.some((event) => event.type === "feed") ? "has-feed" : "",
              dayEvents.some((event) => event.type === "rise") ? "has-rise" : "",
              dayEvents.some((event) => event.type === "peak") ? "has-peak" : "",
              dayEvents.some((event) => event.type === "refeed") ? "has-refeed" : "",
            ].filter(Boolean).join(" ");
            return (
              <button type="button" className={className} key={key} onClick={() => openFeed(key)} aria-label={`${key}: ${dayEvents.length ? calendarDaySummary(dayEvents) : "log feed"}`}>
                <span className="feed-day-number">{day}</span>
                {dayEvents.length ? (
                  <span className="feed-day-events">
                    {visibleEvents.map((event) => (
                      <em className={`feed-event-chip ${event.type}`} key={event.id} title={event.detail}>
                        <span>{event.label}</span>
                        <strong>{event.timeLabel}</strong>
                      </em>
                    ))}
                  </span>
                ) : <Plus size={11} />}
                {dayEvents.length > visibleEvents.length ? <small className="feed-event-more">+{dayEvents.length - visibleEvents.length} more</small> : null}
              </button>
            );
          })}
        </div>
      </section>

      <div className="starter-history">
        <div className="section-title-line">
          <h3>Starter history</h3>
          <span className="starter-history-buttons">
            <button type="button" onClick={() => openFeed()}><CalendarDays size={15} /> Log feed</button>
            <button type="button" onClick={() => openRise()}><TrendingUp size={15} /> Log rise</button>
          </span>
        </div>
        {logs.length ? logs.slice(0, 8).map((log) => {
          const peakEstimate = peakHoursForLog(log, selected, calibration);
          const peakAt = addHours(log.dateTime, peakEstimate.hours);
          const feedAgainAt = addHours(log.dateTime, peakEstimate.hours + feedAgainWindowHours(peakEstimate.hours, log.temperature));
          const logKey = log.id ?? log.dateTime;
          const isRiseCheck = log.entryType === "rise";
          return (
            <div className={`history-row detailed feed-history-card ${isRiseCheck ? "rise-check" : "feed-check"}`} key={logKey}>
              <span className="feed-ratio-badge">{isRiseCheck ? "Rise" : log.ratio}</span>
              <span>
                <strong>{new Date(log.dateTime).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</strong>
                <small>{log.flourBlend?.map((item) => `${item.percent}% ${item.type}`).join(" · ") || selected.flourBlend.map((item) => `${item.percent}% ${item.type}`).join(" · ")}</small>
                <small className="feed-history-schedule">
                  {isRiseCheck
                    ? `Rise check${log.peakHours ? ` · ${log.peakHours}h peak calibrator` : ""}${log.note ? ` · ${log.note}` : ""}`
                    : `Peak ${formatShortTime(peakAt)} · Feed again ${formatShortTime(feedAgainAt)}`}
                </small>
              </span>
              <span className="feed-history-actions">
                <small>{log.peakHours ? `${log.peakHours}h peak` : log.rise ? `${formatRisePercent(log.rise)} rise` : `${log.temperature}°F`}</small>
                {onDeleteStarterLog ? (
                  <button
                    className="feed-log-delete-button"
                    type="button"
                    onClick={() => onDeleteStarterLog(logKey)}
                    aria-label={`Delete ${isRiseCheck ? "rise check" : "feed"} logged ${new Date(log.dateTime).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                ) : null}
              </span>
            </div>
          );
        }) : <EmptyState title="No starter history" body="Log a feed or rise check to start calibrating this starter’s rise estimate." />}
      </div>

      {profileModal ? (
        <StarterProfileModal form={profileModal} setForm={setProfileModal} onClose={() => setProfileModal(null)} onSubmit={saveProfile}>
          {!profileModal.isNew && starters.length > 1 ? confirmDelete ? (
            <div className="delete-confirmation">
              <span>Delete this starter profile?</span>
              <div>
                <button type="button" className="text-button" onClick={() => setConfirmDelete(false)}>Keep it</button>
                <button type="button" className="danger-button" onClick={() => {
                  onDeleteStarter(profileModal.id);
                  setSelectedId(starters.find((item) => item.id !== profileModal.id)?.id || "");
                  setProfileModal(null);
                  setConfirmDelete(false);
                }}>Delete starter</button>
              </div>
            </div>
          ) : (
            <button className="delete-order-button" type="button" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={15} /> Delete starter profile
            </button>
          ) : null}
        </StarterProfileModal>
      ) : null}

      {feedModal ? (
        <Modal title={`Log ${selected.name} ${feedModal.mode === "rise" ? "rise" : "feed"}`} onClose={() => setFeedModal(null)}>
          <form className="form-stack" onSubmit={saveFeed}>
            <p className="starter-feed-science-note">
              {feedModal.mode === "rise"
                ? "Use this when you are checking height, peak timing, bubbles, aroma, or strength without feeding the jar."
                : "Use this when you feed the starter. The app will mark expected peak and feed-again times on the calendar."}
            </p>
            <label>{feedModal.mode === "rise" ? "Check date and time" : "Feed date and time"}<input type="datetime-local" value={feedModal.dateTime} onChange={(event) => setFeedModal({ ...feedModal, dateTime: event.target.value })} /></label>
            {recentFeedForRise ? (
              <div className="starter-rise-elapsed" role="status">
                <span>
                  <Clock3 size={18} />
                  <span>
                    <small>Time since last feed</small>
                    <strong>{formatElapsedSinceFeed(recentFeedForRise.hours)}</strong>
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setFeedModal({ ...feedModal, peakHours: recentFeedForRise.hours.toFixed(1) })}
                >
                  Use {recentFeedForRise.hours.toFixed(1)}h as peak
                </button>
              </div>
            ) : null}
            <div className="form-grid">
              <label>{feedModal.mode === "rise" ? "Last feed ratio" : "Feed ratio"}<input value={feedModal.ratio} onChange={(event) => setFeedModal({ ...feedModal, ratio: event.target.value })} placeholder="1:2:2" /></label>
              <label>{feedModal.mode === "rise" ? "Last feed flour type" : "Feed flour type"}<select value={feedModal.flourType} onChange={(event) => setFeedModal({ ...feedModal, flourType: event.target.value })}>{FLOUR_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
            </div>
            <div className="form-grid">
              <label>Jar temp °F<input type="number" min="45" max="105" value={feedModal.temperature} onChange={(event) => setFeedModal({ ...feedModal, temperature: event.target.value })} /></label>
              <label>Rise %<input type="number" min="0" max="500" step="5" value={feedModal.rise} onChange={(event) => setFeedModal({ ...feedModal, rise: event.target.value })} placeholder="100" /><small className="field-help">100% means the starter doubled in height.</small></label>
            </div>
            <div className="form-grid">
              <label>Hours to peak<input type="number" min="0" step="0.1" value={feedModal.peakHours} onChange={(event) => setFeedModal({ ...feedModal, peakHours: event.target.value })} placeholder="Optional" /></label>
              <span className="starter-feed-science-note">
                {feedModal.mode === "rise"
                  ? recentFeedForRise
                    ? "Use the calculated time above if this is the peak. Saving peak hours calibrates the starter curve."
                    : "If this was the peak, enter hours from feeding to peak. Recent feeds within 12 hours are calculated automatically."
                  : "The saved flour type calibrates the expected peak and feed-again markers."}
              </span>
            </div>
            <label>Notes<textarea value={feedModal.note} onChange={(event) => setFeedModal({ ...feedModal, note: event.target.value })} placeholder="Aroma, bubbles, texture, feeding change…" /></label>
            <button className="primary-button" type="submit">{feedModal.mode === "rise" ? "Save rise check" : "Save feed log"}</button>
          </form>
        </Modal>
      ) : null}
    </section>
  );
}

function StarterProfileModal({ form, setForm, onClose, onSubmit, children }) {
  return (
    <Modal title={form.isNew ? "Add a starter" : `Edit ${form.name}`} onClose={onClose}>
      <form className="form-stack" onSubmit={onSubmit}>
        <label>Starter name<input autoFocus value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Mabel" /></label>
        <div className="form-grid">
          <label>Primary flour<select value={form.primaryFlour} onChange={(event) => setForm({ ...form, primaryFlour: event.target.value })}>{FLOUR_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
          <label>Percent<input type="number" min="0" max="100" value={form.primaryPercent} onChange={(event) => setForm({ ...form, primaryPercent: event.target.value })} /></label>
        </div>
        {form.hasSecondaryFlour ? (
          <div className="starter-secondary-flour">
            <div className="form-grid">
              <label>Second flour<select value={form.secondaryFlour} onChange={(event) => setForm({ ...form, secondaryFlour: event.target.value })}>{FLOUR_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>
              <label>Percent<input type="number" min="0" max="100" value={form.secondaryPercent} onChange={(event) => setForm({ ...form, secondaryPercent: event.target.value })} /></label>
            </div>
            <button type="button" onClick={() => setForm({ ...form, hasSecondaryFlour: false, secondaryPercent: 0 })}><Trash2 size={14} /> Remove second flour</button>
          </div>
        ) : (
          <button className="storage-file-button starter-add-flour" type="button" onClick={() => setForm({ ...form, hasSecondaryFlour: true, secondaryPercent: 20 })}><Plus size={15} /> Add second flour</button>
        )}
        <div className="starter-profile-science">
          <div className="section-title-line"><h3>How this feed behaves</h3><span>Research-informed</span></div>
          <FlourBlendScience compact flours={[form.primaryFlour, form.hasSecondaryFlour && Number(form.secondaryPercent) > 0 ? form.secondaryFlour : null]} />
        </div>
        <label>Starter hydration %<input type="number" min="50" max="200" value={form.hydration} onChange={(event) => setForm({ ...form, hydration: event.target.value })} /></label>
        <label>Profile notes<textarea value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} placeholder="Aroma, personality, maintenance routine…" /></label>
        <button className="primary-button" type="submit">Save starter profile</button>
        {children}
      </form>
    </Modal>
  );
}
